/**
 * Tabbed Content Block.
 */
import $ from 'jquery';
import 'slick-carousel';
import videoSlider from './video-slider-block';
import { Breakpoints } from './breakpoints';
import TableScroll from './tables/tableScroll';

const breakpoints = new Breakpoints();
const mediumBreakpoint = breakpoints.md;

class TabbedContent {
	constructor(container) {
		this.container = container;
		this.containerChildren = Object.values(this.container.children);
		this.labels = [];
		this.content = [];
		this.initalLoad = true; // Gives ability to check if inital script load.
		this.deviceType = '';
		this.handlePageLoad();
		this.tabClickHandler();
		this.accordionTabClickHandler();
		this.key = 0;
		this.target = '';
		this.videoItem = '';
		this.tableTabContainer = '';
		this.handlePageResize();
	}

	setCurrentPageType() {
		this.deviceType = mediumBreakpoint.above() ? 'desktop' : 'mobile';
	}

	handlePageLoad() {
		// Set Device Type.
		this.setCurrentPageType();

		// Acquire Correct Labels and Content.
		this.containerChildren.forEach((child, key) => {
			const values = Object.values(child.children);
			switch (key) {
				case 0:
					this.labels = values;
					break;
				case 1:
					this.content = values;
					break;
				default:
					console.error('No Tabbed Content Block labels or content items found!');
					break;
			}
		});

		// Add loaded class to container.
		setTimeout(() => {
			this.container.classList.add('loaded');
		}, 500);

		// Loading seperatly to allow for table content to load.
		setTimeout(() => {
			if (mediumBreakpoint.below()) {
				this.openAccordionItem();
			}
			this.initalLoad = false;
		}, 500);
	}

	handlePageResize() {
		window.addEventListener('resize', () => {
			const getCurrentDevice = mediumBreakpoint.above() ? 'desktop' : 'mobile';
			if (getCurrentDevice !== this.deviceType) {
				const currentFocusContent = this.container.querySelectorAll('[data-tab-content="true"][data-focus="true"][data-selected="true"]');
				let currentFocusKey = typeof currentFocusContent[0] !== 'undefined' ? currentFocusContent[0].dataset.tabKey : null;
				this.setCurrentPageType();
				switch (this.deviceType) {
					case 'desktop':
						/**
						 * Handles scenario where all accordions are closed and
						 * nothing has focus when switched back to tabs from accordions.
						 */
						if (currentFocusKey === null) {
							currentFocusKey = 0;
						}
						this.clearAccordionOpenItems();
						this.clearFocusedItems();
						this.key = parseInt(currentFocusKey);
						this.addFocusedItems();
						this.addSelected();
						this.reInitTableScroll();
						break;
					case 'mobile':
						this.key = parseInt(currentFocusKey);
						this.clearFocusedItems();
						setTimeout(() => {
							this.initalLoad = true;
							this.addFocusedItems();
							this.openAccordionItem();
							this.reInitTableScroll();
							this.initalLoad = false;
						}, 300);
						break;
					default:
						break;
				}
			}
		});
	}

	tabClickHandler() {
		this.labels.forEach((child) => {
			child.addEventListener('click', (e) => {
				const target = e.target.closest('[data-focus]');
				if (target.dataset.focus === 'false') {
					this.key = target.dataset.tabKey;
					this.clearFocusedItems();
					this.addFocusedItems();
				}
			});
		});
	}

	/**
	 * Handles clicks for mobile accordion.
	 */
	accordionTabClickHandler() {
		this.content.forEach((child) => {
			const contentChildren = Object.values(child.children);
			if (contentChildren.length) {
				const title = contentChildren[0];
				title.addEventListener('click', (e) => {
					// Only enable this functionality on mobile.
					if (mediumBreakpoint.below()) {
						const target = e.target.closest('[data-focus]');
						this.key = target.dataset.tabKey;

						if (target.dataset.focus === 'false') {
							// Show accordion item.
							this.clearSelected();
							this.addFocusedItems();
							this.openAccordionItem();
						} else {
							// Hide items if accordion tab is clicked twice.
							this.clearTab();
						}
					}
				});
			}
		});
	}

	/**
	 * Add Selected attribute to the most recently clicked item.
	 * This is used for mobile->desktop transition where multiple accordion
	 * items have been selected and we choose which one is displaying on desktop.
	 */
	addSelected() {
		const { key, container } = this;
		const currentItems = [...container.querySelectorAll('[data-focus]')];
		if (currentItems.length) {
			currentItems.forEach((item) => {
				const { tabKey, focus } = item.dataset;
				if (parseInt(tabKey) === parseInt(key) && focus === 'true') {
					item.setAttribute('data-selected', 'true');
				}
			});
		} else {
			console.error('Error, data-focus items for data-selected clearing not found.');
		}
	}

	/**
	 * Goes through and clears selected data-selected attributes
	 * which are initalized by a click event.
	 */
	clearSelected() {
		const { container } = this;
		const currentItems = [...container.querySelectorAll('[data-focus]')];
		if (currentItems.length) {
			currentItems.forEach((item) => {
				if (item.dataset.selected === 'true') {
					item.setAttribute('data-selected', 'false');
				}
			});
		} else {
			console.error('Error, data-focus items for data-selected clearing not found.');
		}
	}

	clearTab() {
		const { key, labels, content } = this;
		labels.forEach((label) => {
			const { tabKey } = label.dataset;
			if (tabKey === key) {
				label.setAttribute('data-focus', 'false');
			}
		});

		content.forEach((item) => {
			const { tabKey } = item.dataset;
			const contentChildren = Object.values(item.children);
			const container = contentChildren[1];
			const accordionTab = contentChildren[0];

			if (tabKey === key || !key) {
				accordionTab.setAttribute('data-focus', 'false');
				item.setAttribute('data-focus', 'false');
				$(container).slideUp('fast');
			}
		});
	}

	clearFocusTabs() {
		const { labels } = this;
		labels.forEach((label) => {
			const { focus } = label.dataset;
			if (focus === 'true') {
				label.setAttribute('data-focus', 'false');
			}
		});
	}

	addFocusTab() {
		const { key, labels } = this;
		labels.forEach((label) => {
			const { tabKey } = label.dataset;
			if (parseInt(tabKey) === parseInt(key)) {
				label.setAttribute('data-focus', 'true');
			}
		});
	}

	clearFocusAccordionTabs() {
		const { content } = this;
		content.forEach((item) => {
			const contentChildren = Object.values(item.children);
			contentChildren[0].setAttribute('data-focus', 'false');
		});
	}

	addFocusAccordionTab() {
		const { key, content } = this;
		content.forEach((item) => {
			const contentChildren = Object.values(item.children);
			const accordionTab = contentChildren[0];
			const { tabKey } = item.dataset;
			if (parseInt(tabKey) === parseInt(key)) {
				accordionTab.setAttribute('data-focus', 'true');
			}
		});
	}

	clearFocusContent() {
		const { content } = this;
		content.forEach((item) => {
			const { focus } = item.dataset;
			if (focus === 'true') {
				item.setAttribute('data-focus', 'false');
			}
		});
	}

	addFocusContent() {
		const { key, content } = this;
		content.forEach((item) => {
			const { tabKey } = item.dataset;
			if (parseInt(tabKey) === parseInt(key)) {
				item.setAttribute('data-focus', 'true');
				this.videoItem = item;
				this.reInitSlickVideoSlider();
				this.tableTabContainer = item;
				this.reInitTableScroll();
			}
		});
	}

	reInitSlickVideoSlider() {
		const item = this.videoItem;
		/**
		 * Check and see if video slider is available.
		 * If it is, we reinit slickslider so it displays correctly.
		 */
		const videoSliderArgs = videoSlider.sliderArgs;
		if (typeof videoSliderArgs !== 'undefined') {
			const slider = videoSlider.sliderArgs[0].class;
			if (item.querySelectorAll(slider).length) {
				$(slider).slick('reinit');
			}
		}
	}

	/**
	 * ReIntializes Table Scroll on tables within hidden Tabs.
	 */
	reInitTableScroll() {
		const { tableTabContainer } = this;
		const currentTables = [...tableTabContainer.querySelectorAll('[data-table-wrapper]')];
		try {
			if (currentTables.length) {
				currentTables.map((table) => new TableScroll(table));
			}
		} catch (error) {
			console.error(error);
		}
	}

	clearFocusedItems() {
		this.clearFocusTabs();
		this.clearFocusAccordionTabs();
		this.clearFocusContent();
		this.clearSelected();
	}

	addFocusedItems() {
		this.addFocusTab();
		this.addFocusAccordionTab();
		this.addFocusContent();
		this.addSelected();
	}

	openAccordionItem() {
		const { key, content, initalLoad } = this;
		content.forEach((item) => {
			const { tabKey, focus } = item.dataset;
			const container = Object.values(item.children)[1];
			if ((initalLoad && focus === 'true') || (tabKey === key)) {
				$(container).slideDown('fast');
			}
		});
	}

	clearAccordionOpenItems() {
		const { content } = this;
		content.forEach((item) => {
			const container = Object.values(item.children)[1];
			// We don't use .slideUp() here because it leave behind style='display:none;' which conflicts with other CSS.
			$(container).removeAttr('style');
		});
	}
}

const tabbedContent = {
	init() {
		const containers = [...document.querySelectorAll('.em-tabbed-content__tabs-container')];

		if (containers.length) {
			containers.map((container) => new TabbedContent(container));
		}
	},
};

tabbedContent.init();
